import React, { useContext, useState } from 'react';
import { PrimaryButton } from '../../common';
import greenCheck from '../../../assets/checkmark-secondary.svg';
import caution from '../../../assets/caution-alert.svg';
import './sync-install-updates-card.scss';
import SyncInstallUpdatesCardFileUpload from './sync-install-updates-card-file-upload/sync-install-updates-card-file-upload';
import ServiceHandler from '../../../services/service-handler';
import { useAnalytics } from '../../../hooks/use-analytics';
import { getVinFromLocalStorageIfExists } from '../vehicle-selector-fds/vin-selector/vin-selector';
import { DSL_API_PATH } from '../../../constants';
import {
    notify,
    SlackMessages,
} from '../../../services/slack-message-service/SlackMessageService';
import { SYNC_MAPS } from '../../../support/support-constants';
import localStorageWrapper from '../../utils/local-storage-wrapper/localStorageWrapper';
import useUserProfile from '../../../hooks/use-user-profile';
import ProfileService from '../../../services/profile-service/profile-service';
import serverSideService from '../../../services/server-side-service/server-side-service';
import computerIcon from '../../../assets/computer-icon.svg';
import computerIconLincoln from '../../../assets/computer-icon-lincoln.svg';
import ServerContext from '../../../contexts/serverContext';
import syncDownloadIcon from '../../../assets/sync-download-icon.svg';
import { Modal } from '../../common/modal/modal';
import { SyncInstallUpdatesAddFiles } from './util/sync-install-updates-card-error-message';

interface Props {
    title?: string;
    step?: string;
    osLabel1?: string;
    osLabel2?: string;
    osLabel1Aria?: string;
    osLabel2Aria?: string;
    osLabel1Cta: string;
    osLabel2Cta: string;
    main?: string;
    wifiVideoTutorialLink?: string;
    isLogFileInput: boolean;
    optionalLabel2?: string;
    optionalLabel2Title?: string;
    optionalLabel2Gen3?: string;
    optionalLabel2Subtitle?: string;
    optionalLabel1?: string;
    optionalLabel1Title?: string;
    optionalLabel1Subtitle?: string;
    optionalLabel3Title?: string;
    isTooltip?: boolean;
    tooltipAriaLabel?: string;
    tooltipOSLabel1Content?: string;
    tooltipOSLabel2Content?: string;
    ctaLabel1?: string;
    ctaLabel2?: string;
    ctaLabel1Aria?: string;
    ctaLabel2Aria?: string;
    cta1?: string;
    cta2?: string;
    footerDescription?: string;
    platformLabel?: string;
    isDivider?: boolean;
    downloadUrl?: string;
    accordionType?: string;
    logFileError?: string;
    eulaURL?: string;
    selectedOption?: string;
    pendingLogFileErrorMessage?: string;
    uploadPending?: string;
    xmlVinMismatchMessage?: string;
    genId: string;
}

const SyncInstallUpdatesCard = (props: Props) => {
    const [logFiles, setLogFiles] = useState<FileList | null>();
    const [logFileErrorMessage, setLogFileErrorMessage] = useState<string>('');
    const [modalVisible, setModalVisible] = useState<boolean>(false);

    const [
        pendingLogFileErrorMessage,
        setPendingLogFileErrorMessage,
    ] = useState<string>('');
    const [xmlVinMismatchMessage, setXmlVinMismatchMessage] = useState<string>(
        ''
    );
    const [logFileResponseLoading, setLogFileResponseLoading] = useState<
        boolean
    >(false);
    const profile = useUserProfile();
    const profileService = new ProfileService();
    const serverContext = useContext(ServerContext);
    const vin =
        getVinFromLocalStorageIfExists() ||
        profileService.getUserPreferences(profile?.profile?.email)
            ?.lastSelectedVin;
    const [fireEvents] = useAnalytics();
    const addFiles = async (event: any) => {
        setLogFiles(event?.target?.files);
        if (
            event?.target?.files[0] &&
            event?.target?.files[0].type === 'text/xml'
        ) {
            setLogFileResponseLoading(true);
            setLogFileErrorMessage('');
            setPendingLogFileErrorMessage('');
            setXmlVinMismatchMessage('');
            const uploadResponse = await ServiceHandler.SyncMapService.uploadLogFiles(
                event.target.files[0],
                vin
            );
            const retryUpload =
                vin === localStorageWrapper.getItem('SYNC_UPLOAD_FAILED_1');
            if (uploadResponse.error) {
                const uploadFailureSlackMessage = `${SlackMessages.SYNC_MAPS_FILE_UPLOAD_ERROR} ${vin} with error response: ${uploadResponse.errorDetails} from ${DSL_API_PATH.SYNC_UPLOAD_LOG_FILES}`;
                await SyncInstallUpdatesAddFiles({
                    event: event,
                    vin: vin,
                    setLogFileResponseLoading: setLogFileResponseLoading,
                    setLogFileErrorMessage: setLogFileErrorMessage,
                    setPendingLogFileErrorMessage: setPendingLogFileErrorMessage,
                    setXmlVinMismatchMessage: setXmlVinMismatchMessage,
                    localStorageWrapper: localStorageWrapper,
                    setLogFiles: setLogFiles,
                    xmlVinMismatchMessage: props.xmlVinMismatchMessage,
                    pendingLogFileErrorMessage: pendingLogFileErrorMessage,
                    logFileError: props.logFileError,
                });
                setLogFileResponseLoading(false);
                notify(
                    retryUpload
                        ? `Retried - ${uploadFailureSlackMessage}`
                        : uploadFailureSlackMessage,
                    SYNC_MAPS.SLACK_ALERT_SYNC_MAPS
                );
                localStorageWrapper.setItem('SYNC_UPLOAD_FAILED_1', vin);
            } else {
                setLogFileErrorMessage('');
                setLogFileResponseLoading(false);

                notify(
                    retryUpload
                        ? `Retried - Successful for ${vin}`
                        : `Successful for ${vin}`,
                    SYNC_MAPS.SLACK_ALERT_SYNC_MAPS
                );
                localStorageWrapper.removeItem('SYNC_UPLOAD_FAILED_1');
                serverSideService.isClientSide() &&
                    setTimeout(() => {
                        window.location.reload();
                    }, 2000);
            }
        } else {
            setXmlVinMismatchMessage('');
            setLogFileErrorMessage(props.logFileError || '');
            setLogFileResponseLoading(false);
        }
    };

    const whichCta1Href =
        props?.cta1?.replace('%vin', vin) || props.downloadUrl;

    const addLinksToContent = (content: string): string => {
        return props.eulaURL
            ? content.replace('#fromapi', props.eulaURL)
            : content;
    };

    const step = props.step ? +props.step : 10;
    return (
        <section
            className="sync-install-updates-card"
            data-testid="sync-install-updates-card"
        >
            <div className="sync-install-updates-card-header">
                {props.title && (
                    <div className="circle-title-wrapper">
                        <div className="sync-titles-container">
                            <h3 className="sync-titles-container-title">
                                {props.title}
                            </h3>
                            {props.platformLabel && (
                                <div className="sync-install-updates-card-browse-cta">
                                    <p> {props.platformLabel} </p>
                                    {props.osLabel1 && props.osLabel1Cta && (
                                        <PrimaryButton
                                            color={'dark'}
                                            fill={'outline'}
                                            chevron={false}
                                            href={props.osLabel1Cta}
                                            target="_blank"
                                            onClick={() => {
                                                fireEvents(
                                                    'sync-download-instructions',
                                                    undefined,
                                                    {
                                                        system: props.osLabel1?.toLowerCase(),
                                                        ctaType: props.platformLabel?.toLowerCase(),
                                                    },
                                                    false
                                                );
                                            }}
                                            ariaLabel={props.osLabel1Aria}
                                        >
                                            {props.osLabel1}
                                        </PrimaryButton>
                                    )}

                                    {props.osLabel2 && props.osLabel2Cta && (
                                        <>
                                            &nbsp;|
                                            <PrimaryButton
                                                color={'dark'}
                                                fill={'outline'}
                                                chevron={false}
                                                href={props.osLabel2Cta}
                                                target="_blank"
                                                onClick={() => {
                                                    fireEvents(
                                                        'sync-download-instructions',
                                                        undefined,
                                                        {
                                                            system: props.osLabel2?.toLowerCase(),
                                                            ctaType: props.platformLabel?.toLowerCase(),
                                                        },
                                                        false
                                                    );
                                                }}
                                                ariaLabel={props.osLabel2Aria}
                                            >
                                                {props.osLabel2}
                                            </PrimaryButton>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {(props.optionalLabel1 || props.optionalLabel1Title) && (
                    <div className="optional-labels">
                        <div className="sync-optional-1-and-tooltip">
                            <div
                                className={`optional-label1${
                                    props.optionalLabel1Title
                                        ? ' optional-label-title'
                                        : ''
                                }`}
                            >
                                {!props.optionalLabel1Title &&
                                    props.optionalLabel1}
                                <div className="label1-title">
                                    {props.optionalLabel1Title}
                                </div>
                                <div className="label1-subtitle">
                                    {props.optionalLabel1Subtitle}
                                </div>
                            </div>
                            {props.isTooltip && (
                                <>
                                    <button
                                        onClick={() => setModalVisible(true)}
                                        className="sync-info-icon"
                                        data-testid="sync-info-icon"
                                        aria-label={props.tooltipAriaLabel}
                                        id="sync-info-icon"
                                    >
                                        &#9432;
                                    </button>

                                    <Modal
                                        data-testid="sync-maps-info-modal"
                                        name="sync-maps-info-modal"
                                        isVisible={modalVisible}
                                        onClose={() => {
                                            setModalVisible(false);
                                            document
                                                .getElementById(
                                                    'sync-info-icon'
                                                )
                                                ?.focus();
                                        }}
                                    >
                                        <div className="sync-map-modal-content">
                                            <div className="sync-map-modal-half">
                                                <h2 className="sync-map-half-title">
                                                    {props.osLabel1}
                                                </h2>
                                                <div className="sync-map-half-content">
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html:
                                                                props.tooltipOSLabel1Content ||
                                                                '',
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="sync-map-modal-half">
                                                <h2 className="sync-map-half-title">
                                                    {props.osLabel2}
                                                </h2>
                                                <div className="sync-map-half-content">
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html:
                                                                props.tooltipOSLabel2Content ||
                                                                '',
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </Modal>
                                </>
                            )}
                            {props.optionalLabel1Title && (
                                <div className="maps-divider" />
                            )}
                            <div className="optional-label2">
                                <div className="label2-title">
                                    {props.genId == '3'
                                        ? props.optionalLabel2Gen3
                                        : props.optionalLabel2Title}
                                </div>

                                <div className="label2-subtitle">
                                    {props.optionalLabel2Subtitle}
                                </div>
                            </div>
                            {props.optionalLabel1Title && (
                                <div className="maps-divider" />
                            )}
                            {props.optionalLabel3Title && (
                                <div className="optional-label3">
                                    <div className="label3-title">
                                        <img
                                            src={
                                                serverContext.brand == 'ford'
                                                    ? computerIcon
                                                    : computerIconLincoln
                                            }
                                            className="computer-icon"
                                            loading="lazy"
                                            alt="computer icon"
                                        />
                                    </div>
                                    <div className="label3-subtitle">
                                        {props.optionalLabel3Title}
                                    </div>
                                </div>
                            )}
                        </div>
                        {!props.optionalLabel2Title && (
                            <div>{props.optionalLabel2}</div>
                        )}
                    </div>
                )}
            </div>
            {props.main && (
                <div className="sync-install-updates-card-main">
                    <div
                        className="sync-install-updates-card-main-content "
                        dangerouslySetInnerHTML={{
                            __html: addLinksToContent(props.main),
                        }}
                    />
                    <div>
                        {props.wifiVideoTutorialLink && (
                            <iframe
                                src={`${props.wifiVideoTutorialLink}`}
                                allow="autoplay; encrypted-media"
                                title="video"
                                width="100%"
                                height="400"
                            />
                        )}
                    </div>
                </div>
            )}
            <div className="sync-install-updates-card-footer">
                {props.ctaLabel1 && (
                    <div className="sync-actions">
                        {props.isLogFileInput ? (
                            <SyncInstallUpdatesCardFileUpload
                                ctaLabel1={props.ctaLabel1}
                                logFiles={logFiles}
                                logFileErrorMessage={logFileErrorMessage}
                                warning={caution}
                                greenCheck={greenCheck}
                                addFiles={addFiles}
                                ariaLabel={props.ctaLabel1Aria}
                                pendingLogFileErrorMessage={
                                    pendingLogFileErrorMessage
                                }
                                uploadPending={props.uploadPending}
                                logFileResponseLoading={logFileResponseLoading}
                                xmlVinMismatchMessage={xmlVinMismatchMessage}
                            ></SyncInstallUpdatesCardFileUpload>
                        ) : (
                            <div className="sync-cta-buttons">
                                <PrimaryButton
                                    color={'dark'}
                                    fill={'fill'}
                                    chevron={
                                        props.accordionType == 'maps' ||
                                        props.selectedOption !== 'usb'
                                    }
                                    href={
                                        props?.cta1?.replace('%vin', vin) ||
                                        props.downloadUrl
                                    }
                                    target="_blank"
                                    onClick={() => {
                                        if (
                                            props.downloadUrl &&
                                            props.selectedOption === 'usb'
                                        ) {
                                            fireEvents(
                                                'sync-download-update',
                                                undefined,
                                                undefined,
                                                false
                                            );
                                        } else if (
                                            props.accordionType === 'maps'
                                        ) {
                                            fireEvents(
                                                'sync-search-update',
                                                undefined,
                                                undefined,
                                                false
                                            );
                                        } else if (
                                            props.selectedOption === 'usb' &&
                                            step === 1
                                        ) {
                                            fireEvents(
                                                'sync-download-instructions',
                                                undefined,
                                                {
                                                    system: props.ctaLabel1?.toLowerCase(),
                                                    ctaType:
                                                        props.platformLabel?.toLowerCase() ||
                                                        '',
                                                },
                                                false
                                            );
                                        }
                                    }}
                                    ariaLabel={props.ctaLabel1Aria}
                                >
                                    {props.ctaLabel1}
                                    {props.downloadUrl == whichCta1Href && (
                                        <img
                                            src={syncDownloadIcon}
                                            className="sync-download-icon"
                                            loading="lazy"
                                            alt="sync download icon"
                                        />
                                    )}
                                </PrimaryButton>
                                {props.ctaLabel2 && (
                                    <PrimaryButton
                                        color={'dark'}
                                        fill={'fill'}
                                        chevron={props.selectedOption !== 'usb'}
                                        href={props.cta2}
                                        target="_blank"
                                        ariaLabel={props.ctaLabel2Aria}
                                        onClick={() => {
                                            if (
                                                props.selectedOption ===
                                                    'usb' &&
                                                step === 1
                                            ) {
                                                fireEvents(
                                                    'sync-download-instructions',
                                                    undefined,
                                                    {
                                                        system: props.ctaLabel2?.toLowerCase(),
                                                        ctaType:
                                                            props.platformLabel?.toLowerCase() ||
                                                            '',
                                                    },
                                                    false
                                                );
                                            }
                                        }}
                                    >
                                        {props.ctaLabel2}
                                    </PrimaryButton>
                                )}
                            </div>
                        )}
                    </div>
                )}
                {props.footerDescription && (
                    <div className="sync-install-updates-card-footer-cta">
                        <div
                            className={'sync-install-updates-card-footer-label'}
                            dangerouslySetInnerHTML={{
                                __html: addLinksToContent(
                                    props.footerDescription
                                ),
                            }}
                        />
                    </div>
                )}
            </div>
        </section>
    );
};

export default SyncInstallUpdatesCard;
